import { useEffect } from 'react';

import { ErrorDialogViewComponentNames } from '@fhs-legacy/frontend/src/state/crm-events/constants';
import logger from '@fhs-legacy/frontend/src/utils/logger';

export default function useCartErrorLogging({
  hasError,
  hasPriceError,
  priceMutationError,
  priceOrderFailure,
  unavailableCartEntries,
  calculatedCartTotal,
  shouldSkipPriceOrder,
  isStoreAvailable,
  serviceMode,
  cartEntries,
  isDeliveryOrder,
  cartSubTotal,
  isCartSubTotalNaN,
  cartPriceTooLow,
  checkoutDeliveryPriceMinimum,
  donationAmount,
  isOverCartLimit,
  isOrderingEnabled,
  logErrorDialogView,
}: {
  hasError: boolean;
  hasPriceError: boolean;
  priceMutationError: any;
  priceOrderFailure: boolean;
  unavailableCartEntries: any[];
  calculatedCartTotal: number;
  shouldSkipPriceOrder: boolean;
  isStoreAvailable: boolean;
  serviceMode: any;
  cartEntries: any[];
  isDeliveryOrder: boolean;
  cartSubTotal: number | null | undefined;
  isCartSubTotalNaN: boolean;
  cartPriceTooLow: boolean;
  checkoutDeliveryPriceMinimum: number;
  donationAmount: number;
  isOverCartLimit: boolean;
  isOrderingEnabled: boolean;
  logErrorDialogView: (
    component: ErrorDialogViewComponentNames,
    errorMessage: string | string[],
    paymentErrorFlags?: Record<string, boolean>
  ) => void;
}) {
  useEffect(() => {
    if (hasError) {
      const errorContext = {
        priceMutationError: priceMutationError
          ? {
              message: priceMutationError.message,
              graphQLErrors: priceMutationError.graphQLErrors,
              networkError: priceMutationError.networkError,
            }
          : null,
        priceOrderFailure,
        hasPriceError,

        // Cart state
        unavailableCartEntriesLength: unavailableCartEntries.length,
        isCartTotalNegative: calculatedCartTotal < 0,
        shouldSkipPriceOrder,
        cartEntriesCount: cartEntries.length,
        cartSubTotal,
        isCartSubTotalNaN,

        // Store and service info
        isStoreAvailable,
        serviceMode,
        isDeliveryOrder,

        // Pricing validation
        cartPriceTooLow,
        checkoutDeliveryPriceMinimum,
        donationAmount,

        // Error flags
        isOverCartLimit,
        isOrderingEnabled,

        // Error type classification
        errorTypes: {
          priceError: hasPriceError,
          cartOverLimit: isOverCartLimit,
          orderingDisabled: !isOrderingEnabled,
          storeUnavailable: !isStoreAvailable,
          deliveryPriceTooLow: isDeliveryOrder && !isCartSubTotalNaN && cartPriceTooLow,
        },
      };

      // Determine error type for logging
      let errorType = 'unknown_error';
      let errorMessage = 'Cart Error: Order could not be completed';

      if (hasPriceError) {
        errorType = 'price_mutation_error';
        errorMessage = 'Cart Error: Order could not be completed - Price Mutation Error';
      } else if (isOverCartLimit) {
        errorType = 'cart_over_limit';
        errorMessage = 'Cart Error: Order could not be completed - Cart Over Limit';
      } else if (!isOrderingEnabled) {
        errorType = 'ordering_disabled';
        errorMessage = 'Cart Error: Order could not be completed - Ordering Disabled';
      } else if (!isStoreAvailable) {
        errorType = 'store_unavailable';
        errorMessage = 'Cart Error: Order could not be completed - Store Unavailable';
      } else if (isDeliveryOrder && !isCartSubTotalNaN && cartPriceTooLow) {
        errorType = 'delivery_price_too_low';
        errorMessage = 'Cart Error: Order could not be completed - Delivery Price Too Low';
      }

      // Log error with detailed context
      logger.error({
        message: errorMessage,
        context: {
          ...errorContext,
          isOrderCouldNotBeCompletedError: true,
          errorType,
        },
      });

      // Log CRM event for error dialog view
      logErrorDialogView(ErrorDialogViewComponentNames.CART_ERROR_MODAL, errorMessage);
    }
  }, [
    hasError,
    hasPriceError,
    priceMutationError,
    priceOrderFailure,
    unavailableCartEntries.length,
    calculatedCartTotal,
    shouldSkipPriceOrder,
    isStoreAvailable,
    serviceMode,
    cartEntries.length,
    isDeliveryOrder,
    cartSubTotal,
    isCartSubTotalNaN,
    cartPriceTooLow,
    checkoutDeliveryPriceMinimum,
    donationAmount,
    isOverCartLimit,
    isOrderingEnabled,
    logErrorDialogView,
  ]);
}
